import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';
import 'firebase/compat/messaging';
import 'firebase/compat/analytics';
import 'firebase/compat/database';
import 'firebase/compat/functions';
import { isNativeGoogleSignInAvailable, nativeStartGoogleSignIn } from './utils/nativeGoogleSignIn';

// Configuración e inicialización de Firebase
const firebaseConfig = {
  apiKey: "AIzaSyDqBReTKOd6c9NiY5tYdBMDItOub2gamqk",
  authDomain: "notiline-41ce0.firebaseapp.com",
  databaseURL: "https://notiline-41ce0-default-rtdb.firebaseio.com",
  projectId: "notiline-41ce0",
  storageBucket: "notiline-41ce0.firebasestorage.app",
  messagingSenderId: "1040112227201",
  appId: "1:1040112227201:web:548046361604d502418e80",
  measurementId: "G-MLMRW7ZS4F"
};

// Initialize Firebase
firebase.initializeApp(firebaseConfig);

// Get Firebase service instances
const auth = firebase.auth();
const db = firebase.firestore();
const storage = firebase.storage();
const messaging = firebase.messaging();
const analytics = firebase.analytics();
const functions = firebase.app().functions('us-central1');

// E2E/dev: conecta a los emuladores locales de Auth y Firestore en vez del
// proyecto real (ver playwright.config.js, que ya seteaba esta env var pero
// nunca tenía código acá que la leyera).
if (process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true') {
  auth.useEmulator('http://127.0.0.1:9099', { disableWarnings: true });
  db.useEmulator('127.0.0.1', Number(process.env.REACT_APP_FIRESTORE_EMULATOR_PORT) || 8080);
  // Storage y Realtime Database también: si no, subir fotos y los contadores
  // de planes (publishOffer/publishEvents/publishCar) pegan contra producción
  // con un token del emulador que producción rechaza.
  storage.useEmulator('127.0.0.1', 9199);
  firebase.database().useEmulator('127.0.0.1', 9000);
} else {
  // Cache local en IndexedDB: en visitas repetidas el feed se pinta al toque
  // desde el cache (onSnapshot dispara primero con esos datos y después con
  // los del servidor), en vez de esperar la query completa de offers.
  // No se activa con emuladores para que los tests E2E no arrastren datos viejos.
  // setLogLevel('error') oculta el aviso de deprecación que Firestore imprime
  // al llamar enablePersistence (la API compat no expone FirestoreSettings.cache).
  firebase.firestore.setLogLevel('error');
  // failed-precondition: otra pestaña sin synchronizeTabs; unimplemented: navegador
  // sin IndexedDB. En ambos casos sigue andando sin cache, no hace falta avisar.
  db.enablePersistence({ synchronizeTabs: true }).catch(() => {});
}

export { auth, db, storage, messaging, analytics, functions };

// Reemplaza tu función uploadVideo actual por esta:
export const uploadVideo = async (file) => {
  try {
    // 1. Obtenemos la extensión del archivo original (ej: mp4, mov)
    const extension = file.name.split('.').pop();
    
    // 2. Creamos un nombre único numérico basado en el tiempo actual + aleatorio
    // Ejemplo de resultado: 1732468291000_abc12.mp4
    const uniqueName = `${Date.now()}_${Math.random().toString(36).substr(2, 5)}.${extension}`;
    
    // 3. Referencia con el nuevo nombre limpio
    const storageRef = storage.ref(`videos/${uniqueName}`);
    
    await storageRef.put(file);
    const downloadURL = await storageRef.getDownloadURL();
    return downloadURL;
  } catch (error) {
    console.error("Error uploading video:", error);
    throw error;
  }
};

// ... (resto de tu código) ...

// Redimensiona y comprime la imagen en el navegador antes de subirla. Las fotos
// de celular pesan 3-10 MB y eran el LCP del feed (18 s). GIF/SVG se suben tal cual.
const MAX_IMAGE_SIDE = 1600;
const compressImage = async (file) => {
  if (!file.type?.startsWith('image/') || /gif|svg/.test(file.type)) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_IMAGE_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.8));
    // Safari viejo no soporta webp en toBlob y devuelve png: en ese caso usamos jpeg.
    const finalBlob = blob && blob.type === 'image/webp'
      ? blob
      : await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    if (!finalBlob || finalBlob.size >= file.size) return file;
    const ext = finalBlob.type === 'image/webp' ? 'webp' : 'jpg';
    return new File([finalBlob], file.name.replace(/\.[^.]+$/, '') + '.' + ext, { type: finalBlob.type });
  } catch (error) {
    console.warn('No se pudo comprimir la imagen, se sube la original:', error);
    return file;
  }
};

// Algunas galerías/administradores de archivos de Android entregan la foto sin
// tipo (file.type === ""): no se comprimía y Storage la rechazaba con un error
// de permisos (las reglas de images/ exigen image/*). Se deduce el tipo por la
// extensión; uploadImage solo se usa con inputs accept="image/*", así que si
// la extensión tampoco dice nada se asume jpeg (compressImage la decodifica igual).
const IMAGE_TYPES_BY_EXT = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp',
  gif: 'image/gif', bmp: 'image/bmp', heic: 'image/heic', heif: 'image/heif', avif: 'image/avif',
};
const withImageType = (file) => {
  if (file.type) return file;
  const ext = file.name?.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
  return new File([file], file.name || 'imagen', { type: IMAGE_TYPES_BY_EXT[ext] || 'image/jpeg' });
};

// Mismo límite que storage.rules para images/.
const MAX_IMAGE_UPLOAD_BYTES = 15 * 1024 * 1024;

// Reemplaza tu función uploadImage actual por esta:
export const uploadImage = async (originalFile) => {
  try {
    const file = await compressImage(withImageType(originalFile));
    if (file.size >= MAX_IMAGE_UPLOAD_BYTES) {
      // Si no se pudo comprimir, Storage la rechazaría con un error de permisos
      // que no explica nada: mejor avisar el motivo real.
      throw new Error('La imagen supera los 15 MB y no se pudo reducir. Probá con otra foto.');
    }

    // 1. Extensión del archivo (ej: jpg, png); si el nombre no trae, sale del tipo.
    const extension = file.name.includes('.')
      ? file.name.split('.').pop()
      : file.type.split('/')[1];

    // 2. Creamos un nombre único numérico
    const uniqueName = `${Date.now()}_${Math.random().toString(36).substr(2, 5)}.${extension}`;
    
    const storageRef = storage.ref(`images/${uniqueName}`);
    
    await storageRef.put(file, { contentType: file.type, cacheControl: 'public, max-age=31536000, immutable' });
    const downloadURL = await storageRef.getDownloadURL();
    return downloadURL;
  } catch (error) {
    throw error;
  }
};

export async function saveTokenToFirestore(token) {
  const userId = auth.currentUser ? auth.currentUser.uid : null;

  if (!userId) {
    console.error("Usuario no autenticado");
    return;
  }

  const userRef = db.doc(`users/${userId}`);

  try {
    await userRef.set({ fcmToken: token }, { merge: true });
  } catch (error) {
    console.error("Error al guardar el token FCM en Firestore:", error);
  }
}

// Funciones adicionales relacionadas con la autenticación y la gestión de usuarios

export const getToken = () => {
  return new Promise((resolve, reject) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      if (user) {
        user.getIdToken().then(
          (idToken) => {
            resolve(idToken);
          },
          (error) => {
            resolve(null);
          }
        );
      } else {
        resolve(null);
      }
    });
  });
};

export const token = async () => {
  try {
    const currentUser = auth.currentUser;
    if (currentUser) {
      const idToken = await currentUser.getIdToken();
      return idToken;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error retrieving ID token:", error);
    return null;
  }
};

export const signInUser = async (email, password) => {
  try {
    await auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL);
    return auth.signInWithEmailAndPassword(email, password);
  } catch (error) {
    console.error(error.code, error.message);
    throw error;
  }
};

export const saveUserAddress = async (userId, bsvAddress) => {
  const docRef = db.doc(`users/${userId}`);
  await docRef.set({ bsvAddress }, { merge: true });
};


export const signInWithGoogle = async () => {
  try {
    await auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL);
    let result;
    if (isNativeGoogleSignInAvailable()) {
      const idToken = await nativeStartGoogleSignIn();
      result = await auth.signInWithCredential(
        firebase.auth.GoogleAuthProvider.credential(idToken)
      );
    } else {
      const provider = new firebase.auth.GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      result = await auth.signInWithPopup(provider);
    }
    const user = result.user;
    const userRef = db.doc(`users/${user.uid}`);
    const userDoc = await userRef.get();

    if (!userDoc.exists) {
      // Igual que en createUser: una cuenta nueva no tiene 2FA todavía.
      try {
        localStorage.setItem(`2fa_verified_${user.uid}`, "1");
      } catch (e) {
        // localStorage puede fallar en modo privado; no es crítico
      }
      // El usuario (displayName), teléfono y localidad los completa después
      // en el formulario de registro (ver needsProfile y completeGoogleProfile).
      // Email y teléfono van a users/{uid}/private/contact salvo que el
      // usuario elija mostrarlos (ver utils/contactPrivacy.js): el doc
      // público lo lee cualquier usuario con sesión.
      // Las dos escrituras en paralelo: en el celular cada ida y vuelta pesa.
      await Promise.all([
        userRef.set({
          uid: user.uid,
          displayName: "",
          fullname: user.displayName || "",
          point: "",
          profileImageUrl: user.photoURL || "https://firebasestorage.googleapis.com/v0/b/notiline-vtoisn.appspot.com/o/user.png?alt=media&token=470b8374-53a9-40c6-8013-68a466566fef",
        }),
        db.doc(`users/${user.uid}/private/contact`).set({ email: user.email || "" }, { merge: true }),
      ]);
      return {
        user,
        isNewUser: true,
        needsProfile: true
      };
    }
    // Quien entró con Google y abandonó el formulario vuelve a verlo en el
    // próximo inicio de sesión hasta completar los datos que faltan.
    const data = userDoc.data();
    let phone = data.phone;
    if (!phone) {
      const contactSnap = await db.doc(`users/${user.uid}/private/contact`).get().catch(() => null);
      phone = contactSnap?.exists ? contactSnap.data().phone : "";
    }
    return {
      user,
      isNewUser: false,
      needsProfile: !data.displayName || !phone || !data.point
    };
  } catch (error) {
    console.error("Error en la autenticación con Google:", error);
    throw error;
  }
};

// Guarda los datos del formulario de registro para una cuenta creada con
// "Continuar con Google" (no tiene contraseña: la cuenta ya existe en Auth).
export const completeGoogleProfile = async (uid, displayName, fullname, phone, point) => {
  await db.doc(`users/${uid}`).set(
    { displayName, fullname, point },
    { merge: true }
  );
  await db.doc(`users/${uid}/private/contact`).set({ phone }, { merge: true });
};

export const resetPassword = async (email) => {
  try {
    await auth.sendPasswordResetEmail(email);
  } catch (error) {
    throw new Error(
      "Error al enviar el correo de restablecimiento: " + error.message
    );
  }
};

export const createUser = async (
  email,
  password,
  displayName,
  fullname,
  phone,
  point
) => {
  try {
    const userCredential = await auth.createUserWithEmailAndPassword(
      email,
      password
    );

    const user = userCredential.user;
    const uid = user.uid;

    // Una cuenta recién creada no puede tener 2FA habilitado todavía (eso
    // se activa después desde Ajustes), así que marcamos el dispositivo
    // como verificado de una para que App.js no dispare el chequeo
    // asíncrono de 2FA (con su pantalla de Loading a pantalla completa,
    // por encima del Router) justo cuando navegamos a /home.
    try {
      localStorage.setItem(`2fa_verified_${uid}`, "1");
    } catch (e) {
      // localStorage puede fallar en modo privado; no es crítico
    }

    const userRef = db.doc(`users/${uid}`);
    await userRef.set({
      uid,
      displayName,
      fullname,
      point,
      profileImageUrl:
        "https://firebasestorage.googleapis.com/v0/b/notiline-vtoisn.appspot.com/o/user.png?alt=media&token=470b8374-53a9-40c6-8013-68a466566fef",
    });

    await db.doc(`users/${uid}/private/contact`).set({ email, phone }, { merge: true });

    // Mail de verificación por Resend (functions/index.js); si la función
    // falla, el de Firebase como respaldo. Nada de esto frena el registro
    try {
      await functions.httpsCallable("sendVerificationEmail")();
    } catch (e) {
      console.error("No se pudo enviar el mail de verificación por Resend:", e);
      try {
        await user.sendEmailVerification();
      } catch (e2) {
        console.error("No se pudo enviar el mail de verificación:", e2);
      }
    }

    return userCredential;
  } catch (error) {
    throw error;
  }
};

export const updateUser = async (uid, displayName, phone, point) => {
  try {
    const userRef = db.doc(`users/${uid}`);
    await userRef.update({
      displayName,
      phone,
      point,
    });
    const userData = await userRef.get();
    if (userData.exists) {
      return { uid, ...userData.data() };
    } else {
      throw new Error("El usuario no existe");
    }
  } catch (error) {
    throw error;
  }
};

export const updateUserProfile = async (uid, profileData) => {
  try {
    const userRef = db.doc(`users/${uid}`);
    await userRef.update(profileData);
  } catch (error) {
    console.error("Error al actualizar el perfil del usuario:", error);
    throw error;
  }
};
